/**
 * Internal dependencies
 */
import type {
  JsonObject,
  JsonValue,
  LottieColorArray,
  LottieDefinition,
  LottieElement,
  LottieFont,
  LottieParameter,
  LottieParameterBinding,
  LottieParameterSchema,
  LottieParameterType,
  LottieParameterValue,
  LottieParameterValues,
  LottiePlayback,
  LottiePlaybackEndBehavior,
} from '../types';

const SUPPORTED_PARAMETER_TYPES = new Set<LottieParameterType>([
  'text',
  'number',
  'color',
]);

const SUPPORTED_PLAYBACK_END_BEHAVIORS = new Set<LottiePlaybackEndBehavior>([
  'freeze',
  'reset',
  'hide',
]);

export const DEFAULT_LOTTIE_PLAYBACK: LottiePlayback = {
  autoplay: true,
  loop: false,
  startDelay: 0,
  holdDuration: 0,
  speed: 1,
  endBehavior: 'freeze',
};

const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}([0-9a-f]{2})?$/i;

export interface StudioAnimationDetailDto {
  id: string;
  title: string;
  description?: string | null;
  definition: string;
  width: number;
  height: number;
  duration: number;
  fps: number;
  parameterSchema?: string | null;
  defaultParameterValues?: string | null;
  lottieFonts: LottieFont[];
  previewImageUrl?: string | null;
  hidden: boolean;
}

export type LottieElementSnapshot = Pick<
  LottieElement,
  | 'lottieContractVersion'
  | 'animationId'
  | 'title'
  | 'definition'
  | 'intrinsicWidth'
  | 'intrinsicHeight'
  | 'fps'
  | 'duration'
  | 'parameterSchema'
  | 'defaultParameterValues'
  | 'parameterValues'
  | 'fonts'
  | 'playback'
  | 'previewImageUrl'
>;

export class LottieContractError extends Error {
  constructor(message: string, fieldName: string) {
    super(`${fieldName}: ${message}`);
    this.name = 'LottieContractError';
  }
}

const fail = (fieldName: string, message: string): never => {
  throw new LottieContractError(message, fieldName);
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseJsonObject = (
  value: string,
  fieldName: string
): Record<string, unknown> => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (error) {
    fail(fieldName, 'JSON is invalid.');
  }

  if (!isObject(parsed)) {
    fail(fieldName, 'JSON must be an object.');
  }

  return parsed as Record<string, unknown>;
};

const getRequiredString = (
  object: Record<string, unknown>,
  key: string,
  fieldName: string
): string => {
  const value = object[key];
  if (typeof value !== 'string' || !value.trim()) {
    fail(fieldName, 'Value must be a non-empty string.');
  }

  return value as string;
};

const getOptionalString = (
  object: Record<string, unknown>,
  key: string,
  fieldName: string
): string | undefined => {
  const value = object[key];
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value !== 'string' || !value.trim()) {
    fail(fieldName, 'Value must be a non-empty string when provided.');
  }

  return value as string;
};

const getRequiredNumber = (
  object: Record<string, unknown>,
  key: string,
  fieldName: string
): number => {
  const value = object[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    fail(fieldName, 'Value must be a finite number.');
  }

  return value as number;
};

const getPositiveNumber = (
  object: Record<string, unknown>,
  key: string,
  fieldName: string
): number => {
  const value = getRequiredNumber(object, key, fieldName);
  if (value <= 0) {
    fail(fieldName, 'Value must be greater than 0.');
  }

  return value as number;
};

const getOptionalNumber = (
  object: Record<string, unknown>,
  key: string,
  fieldName: string
): number | undefined => {
  const value = object[key];
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    fail(fieldName, 'Value must be a finite number when provided.');
  }

  return value as number;
};

const getOptionalBoolean = (
  object: Record<string, unknown>,
  key: string,
  fieldName: string
): boolean | undefined => {
  const value = object[key];
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value !== 'boolean') {
    fail(fieldName, 'Value must be boolean when provided.');
  }

  return value as boolean;
};

const isJsonValue = (value: unknown): value is JsonValue => {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.every(isJsonValue);
  }

  return isObject(value) && Object.values(value).every(isJsonValue);
};

const toJsonObject = (
  object: Record<string, unknown>,
  fieldName: string
): JsonObject => {
  if (!isJsonValue(object)) {
    fail(fieldName, 'Object contains unsupported JSON values.');
  }

  return object as JsonObject;
};

const isParameterType = (value: unknown): value is LottieParameterType =>
  typeof value === 'string' &&
  SUPPORTED_PARAMETER_TYPES.has(value as LottieParameterType);

const isPlaybackEndBehavior = (
  value: string
): value is LottiePlaybackEndBehavior =>
  SUPPORTED_PLAYBACK_END_BEHAVIORS.has(value as LottiePlaybackEndBehavior);

const isColorArray = (value: unknown): value is LottieColorArray =>
  Array.isArray(value) &&
  (value.length === 3 || value.length === 4) &&
  value.every((item) => typeof item === 'number' && Number.isFinite(item));

const isHexColor = (value: string) => HEX_COLOR_PATTERN.test(value);

const normalizeBinding = (
  binding: unknown,
  fieldName: string
): LottieParameterBinding => {
  if (!isObject(binding)) {
    fail(fieldName, 'Binding must be an object.');
  }

  const bindingObject = binding as Record<string, unknown>;
  const kind = bindingObject.kind;
  if (kind === 'slot') {
    const slotId = getRequiredString(
      bindingObject,
      'slotId',
      `${fieldName}.slotId`
    );
    const slotType = getOptionalString(
      bindingObject,
      'slotType',
      `${fieldName}.slotType`
    );

    return slotType
      ? { kind: 'slot', slotId, slotType }
      : { kind: 'slot', slotId };
  }

  if (kind === 'jsonPointer') {
    const jsonPointer = getRequiredString(
      bindingObject,
      'jsonPointer',
      `${fieldName}.jsonPointer`
    );

    if (!jsonPointer.startsWith('/')) {
      fail(`${fieldName}.jsonPointer`, 'JSON Pointer must start with "/".');
    }

    return { kind: 'jsonPointer', jsonPointer };
  }

  fail(`${fieldName}.kind`, 'Unsupported binding kind.');
};

const normalizeConstraints = (
  value: unknown,
  fieldName: string
): LottieParameter['constraints'] | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (!isObject(value)) {
    fail(fieldName, 'Constraints must be an object.');
  }

  const constraintsObject = value as Record<string, unknown>;
  const constraints: NonNullable<LottieParameter['constraints']> = {};
  const maxLength = getOptionalNumber(
    constraintsObject,
    'maxLength',
    `${fieldName}.maxLength`
  );
  const min = getOptionalNumber(constraintsObject, 'min', `${fieldName}.min`);
  const max = getOptionalNumber(constraintsObject, 'max', `${fieldName}.max`);

  if (maxLength !== undefined) {
    constraints.maxLength = maxLength;
  }

  if (min !== undefined) {
    constraints.min = min;
  }

  if (max !== undefined) {
    constraints.max = max;
  }

  return Object.keys(constraints).length ? constraints : undefined;
};

const normalizeParameter = (value: unknown, index: number): LottieParameter => {
  const fieldName = `parameterSchema.parameters[${index}]`;

  if (!isObject(value)) {
    fail(fieldName, 'Parameter must be an object.');
  }

  const parameterObject = value as Record<string, unknown>;
  const key = getRequiredString(parameterObject, 'key', `${fieldName}.key`);
  const type = parameterObject.type;
  if (!isParameterType(type)) {
    fail(`${fieldName}.type`, 'Unsupported parameter type.');
  }
  const parameterType = type as LottieParameterType;

  const bindings = parameterObject.bindings;
  if (!Array.isArray(bindings) || !bindings.length) {
    fail(`${fieldName}.bindings`, 'Bindings must be a non-empty array.');
  }
  const parameterBindings = bindings as unknown[];

  const label = getOptionalString(
    parameterObject,
    'label',
    `${fieldName}.label`
  );
  const required = getOptionalBoolean(
    parameterObject,
    'required',
    `${fieldName}.required`
  );
  const constraints = normalizeConstraints(
    parameterObject.constraints,
    `${fieldName}.constraints`
  );

  return {
    key,
    ...(label ? { label } : {}),
    type: parameterType,
    ...(required !== undefined ? { required } : {}),
    ...(constraints ? { constraints } : {}),
    bindings: parameterBindings.map((binding, bindingIndex) =>
      normalizeBinding(binding, `${fieldName}.bindings[${bindingIndex}]`)
    ),
  };
};

const normalizeParameterValue = (
  value: unknown,
  fieldName: string
): LottieParameterValue => {
  if (typeof value === 'string' || typeof value === 'number') {
    if (typeof value === 'number' && !Number.isFinite(value)) {
      fail(fieldName, 'Number value must be finite.');
    }

    return value;
  }

  if (isColorArray(value)) {
    return value;
  }

  fail(fieldName, 'Unsupported parameter value.');
};

const isValueCompatibleWithType = (
  value: LottieParameterValue,
  type: LottieParameterType
) => {
  if (type === 'text') {
    return typeof value === 'string';
  }

  if (type === 'number') {
    return typeof value === 'number';
  }

  if (typeof value === 'string') {
    return isHexColor(value);
  }

  return isColorArray(value);
};

const assertValueMatchesConstraints = (
  parameter: LottieParameter,
  value: LottieParameterValue,
  fieldName: string
) => {
  const { constraints } = parameter;
  if (!constraints) {
    return;
  }

  if (
    parameter.type === 'text' &&
    typeof value === 'string' &&
    constraints.maxLength !== undefined &&
    value.length > constraints.maxLength
  ) {
    fail(fieldName, 'Text value exceeds maxLength constraint.');
  }

  if (parameter.type !== 'number' || typeof value !== 'number') {
    return;
  }

  if (constraints.min !== undefined && value < constraints.min) {
    fail(fieldName, 'Number value is below min constraint.');
  }

  if (constraints.max !== undefined && value > constraints.max) {
    fail(fieldName, 'Number value is above max constraint.');
  }
};

const normalizeValueForParameter = (
  parameter: LottieParameter,
  value: unknown,
  fieldName: string
): LottieParameterValue => {
  const normalizedValue = normalizeParameterValue(value, fieldName);

  if (!isValueCompatibleWithType(normalizedValue, parameter.type)) {
    fail(fieldName, 'Parameter value does not match schema type.');
  }

  assertValueMatchesConstraints(parameter, normalizedValue, fieldName);

  return normalizedValue;
};

const assertValuesMatchSchema = (
  values: LottieParameterValues | undefined,
  schema: LottieParameterSchema | undefined,
  fieldName: string
) => {
  if (!values) {
    return;
  }

  if (!schema) {
    fail(fieldName, 'Parameter values require a parameter schema.');
  }

  const parameterByKey = new Map(
    schema.parameters.map((parameter) => [parameter.key, parameter])
  );

  Object.entries(values).forEach(([key, value]) => {
    const parameter = parameterByKey.get(key);
    if (!parameter) {
      fail(`${fieldName}.${key}`, 'Parameter value key is not in the schema.');
    }

    normalizeValueForParameter(parameter, value, `${fieldName}.${key}`);
  });
};

export const parseLottieDefinition = (value: string): LottieDefinition => {
  const definition = parseJsonObject(value, 'definition');
  const version = getRequiredString(definition, 'v', 'definition.v');
  const width = getPositiveNumber(definition, 'w', 'definition.w');
  const height = getPositiveNumber(definition, 'h', 'definition.h');
  const fps = getPositiveNumber(definition, 'fr', 'definition.fr');
  const inPoint = getOptionalNumber(definition, 'ip', 'definition.ip');
  const outPoint = getRequiredNumber(definition, 'op', 'definition.op');

  if (inPoint !== undefined && outPoint <= inPoint) {
    fail('definition.op', 'Value must be greater than ip.');
  }

  const layers = definition.layers;
  if (!Array.isArray(layers)) {
    fail('definition.layers', 'Layers must be an array.');
  }
  const layersArray = layers as unknown[];

  if (!layersArray.every(isJsonValue)) {
    fail('definition.layers', 'Layers must contain JSON values.');
  }

  const jsonLayers = layersArray as JsonValue[];

  return {
    ...toJsonObject(definition, 'definition'),
    v: version,
    w: width,
    h: height,
    fr: fps,
    ...(inPoint !== undefined ? { ip: inPoint } : {}),
    op: outPoint,
    layers: jsonLayers,
  };
};

export const parseLottieParameterSchema = (
  value?: string | null
): LottieParameterSchema | undefined => {
  if (!value?.trim()) {
    return undefined;
  }

  const schema = parseJsonObject(value, 'parameterSchema');
  if (schema.version !== 1) {
    fail('parameterSchema.version', 'Version must be 1.');
  }

  const schemaParameters = schema.parameters;
  if (!Array.isArray(schemaParameters)) {
    fail('parameterSchema.parameters', 'Parameters must be an array.');
  }
  const schemaParameterArray = schemaParameters as unknown[];

  const parameters = schemaParameterArray.map(normalizeParameter);
  const seenKeys = new Set<string>();
  parameters.forEach((parameter, index) => {
    if (seenKeys.has(parameter.key)) {
      fail(
        `parameterSchema.parameters[${index}].key`,
        'Parameter key must be unique.'
      );
    }

    seenKeys.add(parameter.key);
  });

  return {
    version: 1,
    parameters,
  };
};

export const parseLottieParameterValues = (
  value?: string | null,
  fieldName = 'parameterValues'
): LottieParameterValues | undefined => {
  if (!value?.trim()) {
    return undefined;
  }

  const values = parseJsonObject(value, fieldName);

  return Object.entries(values).reduce<LottieParameterValues>(
    (normalizedValues, [key, parameterValue]) => ({
      ...normalizedValues,
      [key]: normalizeParameterValue(parameterValue, `${fieldName}.${key}`),
    }),
    {}
  );
};

export const normalizeLottieParameterOverride = (
  parameter: LottieParameter,
  value: unknown,
  fieldName = `parameterValues.${parameter.key}`
): LottieParameterValue =>
  normalizeValueForParameter(parameter, value, fieldName);

export const setLottieParameterValue = (
  schema: LottieParameterSchema | undefined,
  values: LottieParameterValues | undefined,
  key: string,
  value: unknown,
  fieldName = 'parameterValues'
): LottieParameterValues => {
  if (!schema) {
    fail(fieldName, 'Parameter values require a parameter schema.');
  }

  const parameter = schema.parameters.find(({ key: schemaKey }) => {
    return schemaKey === key;
  });
  if (!parameter) {
    fail(`${fieldName}.${key}`, 'Parameter value key is not in the schema.');
  }

  return {
    ...(values ?? {}),
    [key]: normalizeLottieParameterOverride(
      parameter,
      value,
      `${fieldName}.${key}`
    ),
  };
};

export const unsetLottieParameterValue = (
  values: LottieParameterValues | undefined,
  key: string
): LottieParameterValues =>
  Object.entries(values ?? {}).reduce<LottieParameterValues>(
    (nextValues, [valueKey, value]) =>
      valueKey === key ? nextValues : { ...nextValues, [valueKey]: value },
    {}
  );

export const normalizeLottiePlayback = (value?: unknown): LottiePlayback => {
  if (value === undefined || value === null) {
    return { ...DEFAULT_LOTTIE_PLAYBACK };
  }

  if (!isObject(value)) {
    fail('playback', 'Playback must be an object.');
  }

  const playbackObject = value as Record<string, unknown>;
  const autoplay =
    getOptionalBoolean(playbackObject, 'autoplay', 'playback.autoplay') ??
    DEFAULT_LOTTIE_PLAYBACK.autoplay;
  const loop =
    getOptionalBoolean(playbackObject, 'loop', 'playback.loop') ??
    DEFAULT_LOTTIE_PLAYBACK.loop;
  const startDelay =
    getOptionalNumber(playbackObject, 'startDelay', 'playback.startDelay') ??
    DEFAULT_LOTTIE_PLAYBACK.startDelay;
  const holdDuration =
    getOptionalNumber(
      playbackObject,
      'holdDuration',
      'playback.holdDuration'
    ) ?? DEFAULT_LOTTIE_PLAYBACK.holdDuration;
  const speed =
    getOptionalNumber(playbackObject, 'speed', 'playback.speed') ??
    DEFAULT_LOTTIE_PLAYBACK.speed;
  const endBehaviorValue = getOptionalString(
    playbackObject,
    'endBehavior',
    'playback.endBehavior'
  );
  let endBehavior: LottiePlaybackEndBehavior =
    DEFAULT_LOTTIE_PLAYBACK.endBehavior;
  if (endBehaviorValue !== undefined) {
    if (!isPlaybackEndBehavior(endBehaviorValue)) {
      fail('playback.endBehavior', 'Unsupported end behavior.');
    }

    endBehavior = endBehaviorValue as LottiePlaybackEndBehavior;
  }
  const loopCount = getOptionalNumber(
    playbackObject,
    'loopCount',
    'playback.loopCount'
  );

  if (startDelay < 0) {
    fail('playback.startDelay', 'Value must be greater than or equal to 0.');
  }

  if (holdDuration < 0) {
    fail('playback.holdDuration', 'Value must be greater than or equal to 0.');
  }

  if (speed <= 0) {
    fail('playback.speed', 'Value must be greater than 0.');
  }

  if (
    loopCount !== undefined &&
    (!Number.isInteger(loopCount) || loopCount < 2)
  ) {
    fail(
      'playback.loopCount',
      'Value must be an integer greater than or equal to 2.'
    );
  }

  return {
    autoplay,
    loop,
    ...(loop && loopCount !== undefined ? { loopCount } : {}),
    startDelay,
    holdDuration,
    speed,
    endBehavior,
  };
};

export const createLottieElementSnapshot = (
  dto: StudioAnimationDetailDto
): LottieElementSnapshot => {
  const dtoRecord = dto as unknown as Record<string, unknown>;
  const definition = parseLottieDefinition(dto.definition);
  const parameterSchema = parseLottieParameterSchema(dto.parameterSchema);
  const defaultParameterValues = parseLottieParameterValues(
    dto.defaultParameterValues,
    'defaultParameterValues'
  );

  assertValuesMatchSchema(
    defaultParameterValues,
    parameterSchema,
    'defaultParameterValues'
  );

  return {
    lottieContractVersion: 1,
    animationId: getRequiredString(dtoRecord, 'id', 'id'),
    title: getRequiredString(dtoRecord, 'title', 'title'),
    definition,
    intrinsicWidth: getPositiveNumber(dtoRecord, 'width', 'width'),
    intrinsicHeight: getPositiveNumber(dtoRecord, 'height', 'height'),
    fps: getPositiveNumber(dtoRecord, 'fps', 'fps'),
    duration: getPositiveNumber(dtoRecord, 'duration', 'duration'),
    ...(parameterSchema ? { parameterSchema } : {}),
    ...(defaultParameterValues ? { defaultParameterValues } : {}),
    parameterValues: {},
    fonts: Array.isArray(dto.lottieFonts) ? dto.lottieFonts : [],
    playback: { ...DEFAULT_LOTTIE_PLAYBACK },
    previewImageUrl: dto.previewImageUrl ?? null,
  };
};

export const getEffectiveLottieParameterValues = (
  element: Pick<LottieElement, 'defaultParameterValues' | 'parameterValues'>
): LottieParameterValues => ({
  ...(element.defaultParameterValues ?? {}),
  ...(element.parameterValues ?? {}),
});
